import { useState, useEffect, Fragment } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import TimeWindow from 'services/timeWindow'
import Select from 'components/Inputs/Select'
import CheckBox from 'components/CheckBox'
import AlertDialog from 'components/AlertDialog'
import MainButton from 'components/Buttons/MainButton'
import professionalWorkingDaysValidator from 'validators/professionalWorkingDaysValidator'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { Formik } from 'formik'

import {
  Container,
  Form,
  Row,
  SubTitle,
  InfoText,
  FormField,
  Label,
  WarningText,
  ErrorContainer
} from '../styles'

export default function EditProfessionalAgenda ({
  professional,
  professionalWorkingDays,
  professionalCategories,
  onWorkingDaysUpdated: handleWorkingDaysUpdated
}) {
  const [loading, setLoading] = useState (true)
  const [businessWorkingDays, setBusinessWorkingDays] = useState ([])
  const [workStartTimeWindows, setWorkStartTimeWindows] = useState ({})
  const [workEndTimeWindows, setWorkEndTimeWindows] = useState ({})
  const [onlineStartTimeWindows, setOnlineStartTimeWindows] = useState ({})
  const [onlineEndTimeWindows, setOnlineEndTimeWindows] = useState ({})
  const [cutileiStartTimeWindows, setCutileiStartTimeWindows] = useState ({})
  const [cutileiEndTimeWindows, setCutileiEndTimeWindows] = useState ({})
  const [errorMessage, setErrorMessage] = useState (null)
  const [showOkDialog, setShowOkDialog] = useState (false)
  const hasEditPermission = AuthService.hasPermission ('business_config.professionals', 'edit')
  const businessId = AuthService.getBusinessId ()
  const token = AuthService.getToken ()
  const navigate = useNavigate ()
  const location = useLocation ()
  const isTutorial = !!location.state?.isTutorial

  useEffect (() => {
    getData ()
  }, [])

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  const getData = async () => {
    try {
      const { data: businessWorkingDays } = await cutileiApi.get (`/businesses/${businessId}/working_days`)

      const timeWindows = {...businessWorkingDays.map (day => TimeWindow.getTimeWindows (day.opening, day.closing))}
      const cutileiTimeWindows = {...businessWorkingDays.map (day => day.accepts_cutilei
        ? TimeWindow.getTimeWindows (day.cutilei_opening, day.cutilei_closing)
        : TimeWindow.getTimeWindows (day.opening, day.closing)
      )}
      const onlineTimeWindows = {...businessWorkingDays.map (day => day.accepts_online
        ? TimeWindow.getTimeWindows (day.online_opening, day.online_closing)
        : TimeWindow.getTimeWindows (day.opening, day.closing)
      )}

      setBusinessWorkingDays (businessWorkingDays)
      setWorkStartTimeWindows (timeWindows)
      setWorkEndTimeWindows (timeWindows)
      setOnlineStartTimeWindows (onlineTimeWindows)
      setOnlineEndTimeWindows (onlineTimeWindows)
      setCutileiStartTimeWindows (cutileiTimeWindows)
      setCutileiEndTimeWindows (cutileiTimeWindows)
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const toggleOkDialog = () => setShowOkDialog (!showOkDialog)

  if (loading) return <Loading style={{marginTop: 30}}/>

  return (
    <Container>
      <Formik
        validationSchema={professionalWorkingDaysValidator}
        initialValues={{
          'working_days': businessWorkingDays.map (day => {
            const workingDay = professionalWorkingDays.find (workingDay => workingDay.id === day.id)
            
            if (workingDay) {
              const work_start = TimeWindow.toShortTime (workingDay.work_start)
              const work_end = TimeWindow.toShortTime (workingDay.work_end)
              const online_start = TimeWindow.toShortTime (workingDay.online_start || workingDay.work_start)
              const online_end = TimeWindow.toShortTime (workingDay.online_end || workingDay.work_end)
              const cutilei_start = TimeWindow.toShortTime (workingDay.cutilei_start || workingDay.work_start)
              const cutilei_end = TimeWindow.toShortTime (workingDay.cutilei_end || workingDay.work_end)

              return { 
                ...workingDay,
                work_start: { label: work_start, value: work_start },
                work_end: { label: work_end, value: work_end },
                online_start: { label: online_start, value: online_start },
                online_end: { label: online_end, value: online_end },
                cutilei_start: { label: cutilei_start, value: cutilei_start },
                cutilei_end: { label: cutilei_end, value: cutilei_end },
                accepts_online: workingDay.accepts_online,
                accepts_cutilei: workingDay.accepts_cutilei,
                selected: true,
                changed: false
              } 
            } else return {
              ...day, work_start: '', work_end: '', accepts_online: false, accepts_cutilei: false,
              online_start: '', online_end: '', cutilei_start: '', cutilei_end: '',
              selected: false, changed: false
            }
          })
        }}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            setErrorMessage (null)
            setSubmitting (true)

            const working_days = values.working_days.map (day => ({
              ...day,
              work_start: TimeWindow.toISOTime (day.work_start.value),
              work_end: TimeWindow.toISOTime (day.work_end.value),
              online_start: TimeWindow.toISOTime (day.online_start.value),
              online_end: TimeWindow.toISOTime (day.online_end.value),
              cutilei_start: TimeWindow.toISOTime (day.cutilei_start.value),
              cutilei_end: TimeWindow.toISOTime (day.cutilei_end.value)
            }))
            
            const selectedDays = working_days.filter (day => day.selected)

            await cutileiApi.post (
              `/businesses/${businessId}/professionals/${professional.id}/working_days`,
              { working_days },
              requestConfig
            )

            setSubmitting (false)
            toggleOkDialog ()
            handleWorkingDaysUpdated (selectedDays)
          } catch (error) {
            setErrorMessage (getApiErrorMessage (error))
            setSubmitting (false)
            logApiError (error)
            logApiError (error)
          }
        }}
      >
        {function WorkingDaysForm ({
          dirty,
          values,
          errors,
          touched,
          isSubmitting,
          setFieldValue,
          setFieldTouched,
          setTouched,
          handleSubmit
        }) {
          const handleDaySelected = (value, index) => {
            setFieldValue (`working_days.${index}`, {
              ...values.working_days[index],
              ...value === true && {
                  work_start: '',
                  work_end: '',
                  online_start: '',
                  online_end: '',
                  cutilei_start: '',
                  cutilei_end: '',
                  accepts_cutilei: false,
                  accepts_online: false
                },
                selected: value,
                changed: true
              })
  
            if (value === true) setTouched ({
              [`working_days.${index}.work_start`]: false,
              [`working_days.${index}.work_end`]: false,
              [`working_days.${index}.online_start`]: false,
              [`working_days.${index}.online_end`]: false,
              [`working_days.${index}.cutilei_start`]: false,
              [`working_days.${index}.cutilei_end`]: false,
              [`working_days.${index}.accepts_online`]: false,
              [`working_days.${index}.accepts_cutilei`]: false
            })
          }

          const handleAcceptsOnlineSelected = (value, index) => {
            setFieldValue (`working_days.${index}`, {
              ...values.working_days[index],
              ...value === true ? {
                online_start: values.working_days[index].work_start,
                online_end: values.working_days[index].work_end
              } : {
                online_opening: '',
                online_end: ''
              },
              accepts_online: value,
              changed: true
            })
  
            if (value === true) setTouched ({
              [`working_days.${index}.online_start`]: false,
              [`working_days.${index}.online_end`]: false
            })
          }
  
          const handleAcceptsCutileiSelected = (value, index) => {
            setFieldValue (`working_days.${index}`, {
              ...values.working_days[index],
              ...value === true ? {
                cutilei_start: values.working_days[index].work_start,
                cutilei_end: values.working_days[index].work_end
              } : {
                cutilei_opening: '',
                cutilei_end: ''
              },
              accepts_cutilei: value,
              changed: true
            })
  
            if (value === true) setTouched ({
              [`working_days.${index}.cutilei_start`]: false,
              [`working_days.${index}.cutilei_end`]: false
            })
          }

          const handleTimeSelected = (name, option) => {
            const dayIndex = parseInt (name.split ('.')[1])
            const businessClosing = TimeWindow.toShortTime (businessWorkingDays[dayIndex].closing)
            const businessOpening = TimeWindow.toShortTime (businessWorkingDays[dayIndex].opening)

            if (name.includes ('work_start')) {
              setWorkEndTimeWindows (prev => ({
                ...prev,
                [dayIndex]: TimeWindow.getTimeWindows (option.value, businessClosing, true)
              }))
              setOnlineStartTimeWindows (prev => ({
                ...prev,
                [dayIndex]: TimeWindow.getTimeWindows (
                  option.value, values.working_days[dayIndex].work_end.value || businessClosing
                )
              }))
              setCutileiStartTimeWindows (prev => ({
                ...prev,
                [dayIndex]: TimeWindow.getTimeWindows (
                  option.value, values.working_days[dayIndex].work_end.value || businessClosing
                )
              }))
            } else if (name.includes ('work_end')) {
              setWorkStartTimeWindows (prev => ({
                ...prev,
                [dayIndex]: TimeWindow.getTimeWindows (businessOpening, option.value, true)
              }))
              setOnlineEndTimeWindows (prev => ({
                ...prev,
                [dayIndex]: TimeWindow.getTimeWindows (
                  values.working_days[dayIndex].work_start.value || businessOpening, option.value
                )
              }))
              setCutileiEndTimeWindows (prev => ({
                ...prev,
                [dayIndex]: TimeWindow.getTimeWindows (
                  values.working_days[dayIndex].work_start.value || businessOpening, option.value
                )
              }))
            }

            setFieldValue ('working_days', values.working_days.map ((day, index) => ({
              ...day,
              work_start: name.includes ('work_start') && !day.work_start
                ? option : index === dayIndex && name.includes ('work_start')
                  ? option : day.work_start,
              work_end: name.includes ('work_end') && !day.work_end
                ? option : index === dayIndex && name.includes ('work_end')
                  ? option : day.work_end,
              changed: index === dayIndex ? true : day.changed
            })))
          }

          const handleOnlineTimeSelected = (name, option) => {
            const dayIndex = parseInt (name.split ('.')[1])
            const lastTimeWindowIndex = onlineEndTimeWindows[dayIndex].length -1

            if (name.includes ('online_opening')) {
              setOnlineEndTimeWindows (prev => ({
                ...prev,
                [dayIndex]: TimeWindow.getTimeWindows (
                  option.value, onlineEndTimeWindows[dayIndex][lastTimeWindowIndex].value, true
                )
              }))
            } else if (name.includes ('online_closing')) {
              setOnlineStartTimeWindows (prev => ({
                ...prev,
                [dayIndex]: TimeWindow.getTimeWindows (
                  onlineStartTimeWindows[dayIndex][0].value, option.value, true
                )
              }))
            }

            setFieldValue ('working_days', values.working_days.map ((day, index) => ({
              ...day,
              online_start: name.includes ('online_start') && day.online_start === ''
                ? option : index === dayIndex && name.includes ('online_start')
                  ? option : day.online_start,
              online_end: name.includes ('online_end') && day.online_end === ''
                ? option : index === dayIndex && name.includes ('online_end')
                  ? option : day.online_end,
              changed: index === dayIndex ? true : day.changed
            })))
          }

          const handleCutileiTimeSelected = (name, option) => {
            const dayIndex = parseInt (name.split ('.')[1])
            const lastTimeWindowIndex = cutileiEndTimeWindows[dayIndex].length -1

            if (name.includes ('cutilei_opening')) {
              setCutileiEndTimeWindows ( prev => ({
                ...prev,
                [dayIndex]: TimeWindow.getTimeWindows (
                  option.value, cutileiEndTimeWindows[dayIndex][lastTimeWindowIndex].value, true
                )
              }))
            } else if (name.includes ('cutilei_closing')) {
              setCutileiStartTimeWindows (prev => ({
                ...prev,
                [dayIndex]: TimeWindow.getTimeWindows (
                  cutileiStartTimeWindows[dayIndex][0].value, option.value, true
                )
              }))
            }

            setFieldValue ('working_days', values.working_days.map ((day, index) => ({
              ...day,
              cutilei_start: name.includes ('cutilei_start') && day.cutilei_start === ''
                ? option : index === dayIndex && name.includes ('cutilei_start')
                  ? option : day.cutilei_start,
              cutilei_end: name.includes ('cutilei_end') && day.cutilei_end === ''
                ? option : index === dayIndex && name.includes ('cutilei_end')
                  ? option : day.cutilei_end,
              changed: index === dayIndex ? true : day.changed
            })))
          }

          return (
            <Form>
              <SubTitle>Disponibilidade de atendimento da agenda do profissional</SubTitle>
              <Row style={{alignItems: 'flex-start'}}>
                {values.working_days.map ((day, index) => (
                  <FormField key={day.id}>
                    <Row style={{marginBottom: -5, alignItems: 'center'}}>
                      <CheckBox
                        id={`working_days.${index}.selected`}
                        style={{marginRight: 6}}
                        value={day.selected}
                        onValueChange={value => handleDaySelected (value, index)}
                        disabled={!hasEditPermission}
                      />
                      <Label htmlFor={`working_days.${index}.selected`}>
                        {day.name}
                      </Label>
                    </Row>
                    {day.selected && (
                      <>
                        <Select
                          name={`working_days.${index}.work_start`}
                          placeholder='Início...'
                          value={day.work_start}
                          options={workStartTimeWindows[index]}
                          onChange={handleTimeSelected}
                          onBlur={setFieldTouched}
                          containerStyles={{marginTop: '10px'}}
                          error={errors.working_days?.[index]}
                          touched={touched.working_days?.[index]}
                          isDisabled={!hasEditPermission}
                        />
                        {touched.working_days?.[index]?.work_start && errors.working_days?.[index]?.work_start && (
                          <WarningText>{errors.working_days[index].work_start}</WarningText>
                        )}
                        <Select
                          name={`working_days.${index}.work_end`}
                          placeholder='Término...'
                          value={day.work_end}
                          options={workEndTimeWindows[index]}
                          onChange={handleTimeSelected}
                          onBlur={setFieldTouched}
                          containerStyles={{marginTop: '10px'}}
                          error={errors.working_days?.[index]}
                          touched={touched.working_days?.[index]}
                          isDisabled={!hasEditPermission}
                        />
                        {touched.working_days?.[index]?.work_end && errors.working_days?.[index]?.work_end && (
                          <WarningText>{errors.working_days[index].work_end}</WarningText>
                        )}
                      </>
                    )}
                  </FormField>
                ))}
              </Row>
              {values.working_days.some (day => day.selected) && (
                <Fragment>
                  {professionalCategories.some (c => c.name.toLowerCase () === 'manicure e pedicure') &&
                  businessWorkingDays.some ((day, index) => (
                    day.accepts_cutilei && values.working_days[index].selected
                  )) && (
                    <Fragment>
                      <SubTitle>Disponibilidade de atendimento para a Cutilei</SubTitle>
                      <Row style={{alignItems: 'flex-start'}}>
                        {values.working_days.map ((day, index) => (
                          day.selected && businessWorkingDays[index].accepts_cutilei ? (
                            <FormField key={index}>
                              <Row style={{marginBottom: -5, alignItems: 'center'}}>
                                <CheckBox
                                  id={`working_days.${index}.accepts_cutilei`}
                                  style={{marginTop: 3, marginRight: 6}}
                                  value={day.accepts_cutilei}
                                  onValueChange={value => handleAcceptsCutileiSelected (value, index)}
                                  disabled={!hasEditPermission}
                                />
                                <Label htmlFor={`working_days.${index}.accepts_cutilei`}>
                                  {day.name}
                                </Label>
                              </Row>
                              {businessWorkingDays[index].accepts_cutilei && day.accepts_cutilei && (
                                <Fragment>
                                  <Select
                                    name={`working_days.${index}.cutilei_start`}
                                    placeholder='Abertura...'
                                    value={day.cutilei_start}
                                    options={cutileiStartTimeWindows[index]}
                                    onChange={handleCutileiTimeSelected}
                                    onBlur={setFieldTouched}
                                    containerStyles={{marginTop: '10px'}}
                                    error={errors.working_days?.[index]}
                                    touched={touched.working_days?.[index]}
                                    isDisabled={!hasEditPermission}
                                  />
                                  {touched.working_days?.[index]?.cutilei_start && errors.working_days?.[index]?.cutilei_start && (
                                    <WarningText>{errors.working_days[index].cutilei_start}</WarningText>
                                  )}
                                  <Select
                                    name={`working_days.${index}.cutilei_end`}
                                    placeholder='Fechamento...'
                                    value={day.cutilei_end}
                                    options={cutileiEndTimeWindows[index]}
                                    onChange={handleCutileiTimeSelected}
                                    onBlur={setFieldTouched}
                                    containerStyles={{marginTop: '10px'}}
                                    error={errors.working_days?.[index]}
                                    touched={touched.working_days?.[index]}
                                    isDisabled={!hasEditPermission}
                                  />
                                  {touched.working_days?.[index]?.cutilei_end && errors.working_days?.[index]?.cutilei_end && (
                                    <WarningText>{errors.working_days[index].cutilei_end}</WarningText>
                                  )}
                                </Fragment>
                              )}
                            </FormField>
                          ) : (
                            <FormField key={index}/>
                          )
                        ))}
                      </Row>
                    </Fragment>
                  )}
                  {businessWorkingDays.some ((day, index) => (
                    day.accepts_online && values.working_days[index].selected
                  )) && (
                    <Fragment>
                      <SubTitle>Disponibilidade de atendimento para agendamentos online</SubTitle>
                      <Row style={{alignItems: 'flex-start', marginBottom: 3}}>
                        {values.working_days.map ((day, index) => (
                          day.selected && businessWorkingDays[index].accepts_online ? (
                            <FormField key={index}>
                              <Row style={{marginBottom: -5, alignItems: 'center'}}>
                                <CheckBox
                                  id={`working_days.${index}.accepts_online`}
                                  style={{marginTop: 3, marginRight: 6}}
                                  value={day.accepts_online}
                                  onValueChange={value => handleAcceptsOnlineSelected (value, index)}
                                  disabled={!hasEditPermission}
                                />
                                <Label htmlFor={`working_days.${index}.accepts_online`}>
                                  {day.name}
                                </Label>
                              </Row>
                              {businessWorkingDays[index].accepts_online && day.accepts_online && (
                                <Fragment>
                                  <Select
                                    name={`working_days.${index}.online_start`}
                                    placeholder='Abertura...'
                                    value={day.online_start}
                                    options={onlineStartTimeWindows[index]}
                                    onChange={handleOnlineTimeSelected}
                                    onBlur={setFieldTouched}
                                    containerStyles={{marginTop: '10px'}}
                                    error={errors.working_days?.[index]}
                                    touched={touched.working_days?.[index]}
                                    isDisabled={!hasEditPermission}
                                  />
                                  {touched.working_days?.[index]?.online_start && errors.working_days?.[index]?.online_start && (
                                    <WarningText>{errors.working_days[index].online_start}</WarningText>
                                  )}
                                  <Select
                                    name={`working_days.${index}.online_end`}
                                    placeholder='Fechamento...'
                                    value={day.online_end}
                                    options={onlineEndTimeWindows[index]}
                                    onChange={handleOnlineTimeSelected}
                                    onBlur={setFieldTouched}
                                    containerStyles={{marginTop: '10px'}}
                                    error={errors.working_days?.[index]}
                                    touched={touched.working_days?.[index]}
                                    isDisabled={!hasEditPermission}
                                  />
                                  {touched.working_days?.[index]?.online_end && errors.working_days?.[index]?.online_end && (
                                    <WarningText>{errors.working_days[index].online_end}</WarningText>
                                  )}
                                </Fragment>
                              )}
                            </FormField>
                          ) : (
                            <FormField key={index}/>
                          )
                        ))}
                      </Row>
                    </Fragment>
                  )}
                </Fragment>
              )}
              {errorMessage && (
                <ErrorContainer>
                  <p>{errorMessage}</p>
                </ErrorContainer>
              )}
              <AlertDialog
                visible={showOkDialog}
                title='Sucesso'
                message={
                  isTutorial ? <Fragment>
                    <InfoText>
                      Agenda do profissional alterada!
                      Deseja voltar para o guia de configuração do salão ou continuar editando a agenda?
                    </InfoText>
                  </Fragment> : 'Agenda do profissional alterada!'
                }
                confirmText={isTutorial ? 'Voltar para o guia' : 'Ok'}
                cancelText={isTutorial ? 'Continuar na agenda' : ''}
                onConfirm={isTutorial ? () => navigate ('/business', { replace: true }) : toggleOkDialog}
                onClose={toggleOkDialog}
                containerStyle={{width: isTutorial ? 400 : 360}}
                buttonStyle={{width: isTutorial ? 160 : 100}} 
              />
              {hasEditPermission && dirty && (
                <MainButton
                  label='Salvar alterações'
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  loading={isSubmitting}
                  width={160}
                  style={{alignSelf: 'center', marginTop: 15}}
                />
              )}
            </Form>
          )
        }}
      </Formik>
    </Container>
  )
}
